/**
 * Platform announcement banner for the Email console.
 *
 * ═══ One announcement system, both products ══════════════════════════════════
 *
 * Staff post a banner once, under Platform Messaging on the admin console. It writes to the
 * same `announcements` table the AI dashboard reads — and now this console reads it too, scoped
 * to `?product=email` so an AI-only notice never lands here and an email-pipeline outage notice
 * does. That is the "message either the AI or the Email platform" the product asked for.
 *
 * ═══ It is furniture, never a failure mode ═══════════════════════════════════
 *
 * A banner is decoration on top of the product. Every failure path here renders NOTHING: a
 * failed fetch, an expired session, a private-mode localStorage throw — none of them may keep
 * the console from loading. The read is already audience-filtered and schedule-filtered on the
 * server, so the client only decides dismissal.
 *
 * Dismissal is per-browser, keyed on `${id}:${revision}` so editing a banner's wording brings it
 * back for someone who dismissed the old text — "resolved" must not stay hidden behind a
 * dismissal of "investigating".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type ApiAnnouncement } from "../lib/api";
import { isSignedIn } from "../lib/session";
import { Icon, type IconName } from "./Icon";

const STORAGE_KEY = "pa_email_dismissed_announcements";
const POLL_MS = 120_000;

type Level = ApiAnnouncement["level"];

const LEVEL: Record<Level, { icon: IconName; fg: string; bg: string; border: string; label: string; live: "polite" | "assertive" }> = {
  info:    { icon: "info",     fg: "var(--accent)",  bg: "var(--accent-subtle)",  border: "var(--border)",      label: "Update",         live: "polite" },
  warning: { icon: "warning",  fg: "var(--warning)", bg: "var(--warning-subtle)", border: "var(--warn-border)", label: "Service notice", live: "polite" },
  error:   { icon: "critical", fg: "var(--danger)",  bg: "var(--danger-subtle)",  border: "var(--danger)",      label: "Incident",       live: "assertive" },
};

function dismissalKey(a: Pick<ApiAnnouncement, "id" | "revision">): string {
  return `${a.id}:${a.revision}`;
}

function readDismissed(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeDismissed(keys: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys.slice(-50)));
  } catch {
    /* private mode — the banner simply reappears next load */
  }
}

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function AnnouncementBanner() {
  const [items, setItems] = useState<ApiAnnouncement[]>([]);
  const [dismissed, setDismissed] = useState<string[]>(() =>
    typeof window === "undefined" ? [] : readDismissed(),
  );

  // Poll. A banner about the email pipeline should reach an already-open console
  // without a reload, but not chattily — 120s matches the AI dashboard.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!isSignedIn()) { if (alive) setItems([]); return; }
      try {
        const res = await api.announcements();
        if (alive) setItems(res?.announcements ?? []);
      } catch {
        // Never surface — furniture, not an error state.
        if (alive) setItems([]);
      }
    };
    void load();
    const timer = window.setInterval(load, POLL_MS);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Keep dismissal in step across tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setDismissed(readDismissed());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const dismiss = useCallback((a: ApiAnnouncement) => {
    setDismissed((prev) => {
      const next = prev.includes(dismissalKey(a)) ? prev : [...prev, dismissalKey(a)];
      writeDismissed(next);
      return next;
    });
  }, []);

  const visible = useMemo(
    () => items.filter((a) => !a.dismissible || !dismissed.includes(dismissalKey(a))),
    [items, dismissed],
  );

  if (visible.length === 0) return null;

  return (
    <div>
      {visible.map((a) => {
        const s = LEVEL[a.level] ?? LEVEL.info;
        return (
          <div
            key={dismissalKey(a)}
            role="status"
            aria-live={s.live}
            className="flex items-center gap-2.5"
            style={{
              background: s.bg,
              borderBottom: `1px solid ${s.border}`,
              padding: "9px 16px",
            }}
            data-testid={`announcement-${a.level}`}
          >
            <Icon name={s.icon} size={15} style={{ color: s.fg, flexShrink: 0 }} />
            <span
              className="shrink-0"
              style={{ fontSize: "var(--text-micro)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: s.fg }}
            >
              {s.label}
            </span>
            <span className="truncate" style={{ fontSize: "var(--text-dense)", color: "var(--text)", fontWeight: 500 }}>
              {a.message}
            </span>
            {a.actionUrl && a.actionLabel && (
              <a
                href={a.actionUrl}
                {...(isExternal(a.actionUrl) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="shrink-0"
                style={{ fontSize: "var(--text-dense)", fontWeight: 600, color: s.fg, textDecoration: "underline" }}
              >
                {a.actionLabel}
                <span aria-hidden="true"> →</span>
              </a>
            )}
            {a.dismissible && (
              <button
                type="button"
                onClick={() => dismiss(a)}
                aria-label="Dismiss announcement"
                className="ml-auto flex shrink-0 items-center"
                style={{ color: s.fg, opacity: 0.8, padding: 4 }}
              >
                <Icon name="close" size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default AnnouncementBanner;
